import React from "react";
import { C, radii, shadows, space, type } from "../../styles/tokens";
import { StepProgress } from "./StepProgress";

const TONES = {
  warning: { fill: C.warningTint, color: C.warning },
  success: { fill: C.successTint, color: C.success },
  info: { fill: C.infoTint, color: C.navy },
};

const BADGE_POSITIONS = [
  { left: "8%", top: 20 },
  { right: "10%", top: 0 },
  { right: "14%", bottom: 0 },
];

/**
 * StepIntro (docs/BRAND_MANUAL.md, section 8)
 * Top of a sign-up step or focused action page:
 * - 190px disc on primaryTint with 84px primary icon, up to 3 floating badges (56px) on warning, success, info tints
 * - Compact mode (for forms): 72px disc with 32px icon, no badges
 * - StepProgress dots when step & total provided
 * - Centred display or title headline and supporting body text
 */
export function StepIntro({
  step,
  total,
  Icon,
  badges = [],
  title,
  body,
  compact = false,
  style = {},
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginBottom: compact ? space.lg : space.xl,
        textAlign: "center",
        ...style,
      }}
    >
      {/* Illustration Scene */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 320,
          height: compact ? 88 : 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: compact ? space.sm : space.md,
        }}
        aria-hidden="true"
      >
        {/* Central Disc */}
        <div
          style={{
            width: compact ? 72 : 190,
            height: compact ? 72 : 190,
            borderRadius: radii.pill,
            backgroundColor: C.primaryTint,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {Icon && (
            <Icon
              size={compact ? 32 : 84}
              strokeWidth={compact ? 2 : 1.5}
              color={C.primary}
            />
          )}
        </div>

        {/* Floating Badges */}
        {!compact &&
          badges.slice(0, 3).map(({ key, Icon: BadgeIcon, tone = "info" }, idx) => {
            const toneConfig = TONES[tone] || TONES.info;
            const pos = BADGE_POSITIONS[idx] || BADGE_POSITIONS[0];

            return (
              <div
                key={key || idx}
                style={{
                  position: "absolute",
                  ...pos,
                  width: 56,
                  height: 56,
                  borderRadius: radii.pill,
                  backgroundColor: toneConfig.fill,
                  boxShadow: shadows.raised,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: toneConfig.color,
                }}
              >
                {BadgeIcon && <BadgeIcon size={24} strokeWidth={2} />}
              </div>
            );
          })}
      </div>

      {/* Step Progress Dots */}
      {step && total && <StepProgress step={step} total={total} />}

      {/* Headline Title */}
      {title && (
        <h1
          style={{
            margin: 0,
            color: C.navy,
            fontSize: compact ? type.title.fontSize : type.display.fontSize,
            lineHeight: compact ? type.title.lineHeight : type.display.lineHeight,
            fontWeight: compact ? type.title.fontWeight : type.display.fontWeight,
            letterSpacing: type.display.letterSpacing,
          }}
        >
          {title}
        </h1>
      )}

      {/* Supporting Body / Caption */}
      {body && (
        <p
          style={{
            margin: 0,
            marginTop: space.sm,
            color: C.textMuted,
            fontSize: type.body.fontSize,
            lineHeight: type.body.lineHeight,
            fontWeight: type.body.fontWeight,
            maxWidth: 480,
          }}
        >
          {body}
        </p>
      )}
    </div>
  );
}
